import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Divider,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { ArrowBack, CloudUpload, Delete } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useFileUpload } from '../hooks/useFileUpload';
import UploadProgressIndicator from '../components/common/UploadProgressIndicator';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function CreateProject() {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user, role } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [files, setFiles] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [creationCompleted, setCreationCompleted] = useState(false);

  // Refs to hold current values for cleanup function
  const projectIdRef = useRef(projectId);
  const creationCompletedRef = useRef(creationCompleted);

  // Update refs when state changes
  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  useEffect(() => {
    creationCompletedRef.current = creationCompleted;
  }, [creationCompleted]);

  const steps = ['Project Details', 'Add Members', 'Upload Files'];

  const showErrorCallback = useCallback((message) => {
    setError(message);
  }, [setError]);

  const afterUploadCallback = useCallback(() => {
    if (projectId) {
      navigate(`/projects?projectId=${projectId}`);
    }
  }, [navigate, projectId]);

  const { uploadProgress, uploadFiles } = useFileUpload(
    projectId,
    afterUploadCallback,
    showErrorCallback
  );

  // Function to delete project by ID
  const deleteProjectById = useCallback(async (idToDelete) => {
    if (!idToDelete) return;
    console.log(`Attempting to delete project: ${idToDelete}`);
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', idToDelete);
      if (error) throw error;
      console.log(`Project ${idToDelete} deleted successfully.`);
    } catch (error) {
      console.error(`Error deleting project ${idToDelete}:`, error);
      // Optionally show an error to the user, although it might be during navigation/unload
      // setError(`Kon project ${idToDelete} niet opruimen: ${error.message}`);
    }
  }, []);

  useEffect(() => {
    if (activeStep === 1) {
      let ignore = false;

      const fetchUsersWrapper = async () => {
        setLoading(true);
        try {
          const { data: response, error: functionError } = await supabase.functions.invoke('list-users', {
            method: 'GET'
          });

          if (functionError) throw functionError;
          if (response.status === 'error') throw new Error(response.message);

          if (!ignore) {
            setUsers(response.data.filter(u => u.id !== user.id && u.role !== 'admin'));
          }
        } catch (error) {
          console.error('Error fetching users:', error);
          if (!ignore) {
            setError(error.message);
          }
        } finally {
          if (!ignore) {
            setLoading(false);
          }
        }
      };

      fetchUsersWrapper();

      return () => {
        ignore = true;
      };
    }
  }, [activeStep, user?.id, supabase, setLoading, setUsers, setError]); // Adjusted dependencies for fetching users

  // Cleanup project if creation is incomplete when the component unmounts
  useEffect(() => {
    return () => {
      const currentProjectId = projectIdRef.current;
      const isCompleted = creationCompletedRef.current;
      
      if (currentProjectId && !isCompleted) {
        console.log(`CreateProject: Component unmounting, cleaning up incomplete project: ${currentProjectId}`);
        deleteProjectById(currentProjectId);
      }
    };
  }, [deleteProjectById]); // Depends on deleteProjectById

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleCreateProject = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Create project
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .insert([{
          name,
          description,
          created_by: user.id,
          status: 'active'
        }])
        .select()
        .single();

      if (projectError) throw projectError;

      setProjectId(projectData.id);
      setActiveStep(1);
    } catch (err) {
      console.error('Failed to create project:', err);
      setError(`Error: ${err.message || 'Onbekende fout bij het aanmaken van project'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMembers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Add users to project
      if (selectedUsers.length > 0) {
        const projectMembers = selectedUsers.map(userId => ({
          project_id: projectId,
          user_id: userId,
          role: 'member',
          created_by: user.id
        }));

        const { error: userError } = await supabase
          .from('project_members')  // Changed from 'project_users' to 'project_members'
          .insert(projectMembers);

        if (userError) throw userError;
      }
      
      setActiveStep(2);
    } catch (err) {
      console.error('Failed to add members:', err);
      setError(`Error: ${err.message || 'Onbekende fout bij het toevoegen van leden'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFiles = async () => {
    if (files.length > 0 && projectId) {
      setCreationCompleted(true);
      await uploadFiles(files);
    }
  };

  const skipFilesAndFinish = () => {
    setCreationCompleted(true);
    navigate(`/projects?projectId=${projectId}`);
  };

  if (role !== 'admin' && role !== 'project_manager') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Je hebt geen toegang tot deze pagina
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ p: 3, mt: '80px' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton 
          onClick={async () => {
            if (projectIdRef.current) {
              await deleteProjectById(projectIdRef.current);
            }
            navigate('/projects');
          }} 
          sx={{ p: 0 }}
          color="primary"
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">
          Nieuw Project
        </Typography>
      </Box>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {activeStep === 0 && (
        /* Step 1: Project Details Form */
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Project Details
          </Typography>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Projectnaam"
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Beschrijving"
              variant="outlined"
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />
          </Box>
          
          {/* Action Buttons for Step 1 */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined"
              onClick={() => navigate('/projects')}
              disabled={loading}
            >
              Annuleren
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateProject}
              disabled={!name || loading}
            >
              {loading ? 'Project Aanmaken...' : 'Project Aanmaken'}
            </Button>
          </Box>
        </Paper>
      )}

      {activeStep === 1 && (
        /* Step 2: User Selection Table */
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Projectleden Toevoegen
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                      checked={selectedUsers.length === users.length && users.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers(users.map(u => u.id));
                        } else {
                          setSelectedUsers([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Rol</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      <Typography color="textSecondary" display="inline">
                        Gebruikers laden...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => {
                            setSelectedUsers(prev => 
                              prev.includes(user.id)
                                ? prev.filter(id => id !== user.id)
                                : [...prev, user.id]
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.role}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Action Buttons for Step 2 */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined"
              onClick={async () => {
                if (projectId) {
                  setLoading(true);
                  setError(null);
                  try {
                    await deleteProjectById(projectId);
                    setProjectId(null); // Reset projectId state
                    setActiveStep(0);
                  } catch (err) {
                    setError(`Kon project niet verwijderen: ${err.message}`);
                  } finally {
                    setLoading(false);
                  }
                } else {
                  setActiveStep(0);
                }
              }}
              disabled={loading}
            >
              Terug
            </Button>
            <Button
              variant="contained"
              onClick={handleAddMembers}
              disabled={loading}
            >
              {loading ? 'Leden Toevoegen...' : 'Volgende'}
            </Button>
          </Box>
        </Paper>
      )}

      {activeStep === 2 && (
        /* Step 3: File Upload */
        <>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">
              Bestanden Toevoegen (Optioneel)
            </Typography>
          </Box>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUpload />}
              >
                Bestanden Kiezen
                <VisuallyHiddenInput
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  accept="application/pdf"
                />
              </Button>

              {files.length > 0 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Bestandsnaam</TableCell>
                        <TableCell>Grootte</TableCell>
                        <TableCell align="right">Acties</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {files.map((file, index) => (
                        <TableRow key={index}>
                          <TableCell>{file.name}</TableCell>
                          <TableCell>{(file.size / 1024).toFixed(2)} KB</TableCell>
                          <TableCell align="right">
                            <IconButton onClick={() => handleRemoveFile(index)}>
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Paper>

          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => setActiveStep(1)}
              disabled={loading || uploadProgress.visible}
            >
              Terug
            </Button>
            <Button
              variant="outlined"
              onClick={skipFilesAndFinish}
              disabled={loading || uploadProgress.visible}
            >
              Overslaan & Voltooien
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadFiles}
              disabled={loading || files.length === 0 || uploadProgress.visible}
            >
              {uploadProgress.visible ? 'Bestanden Uploaden...' : 'Voltooien & Bestanden Uploaden'}
            </Button>
          </Box>
        </>
      )}

      <UploadProgressIndicator uploadProgress={uploadProgress} />
    </Box>
  );
}
